import React, { useState, useEffect } from 'react';
import { Modal, Steps, Button, message } from 'antd';

import CampaignTypeStep from './CampaignTypeStep';
import CampaignDetailsStep from './CampaignDetailsStep';
import WorkflowsStep from './WorkflowsStep';
import ScheduleStep from './ScheduleStep';
import SuccessAnimation from './SuccessAnimation';
import { useSmartVariables } from 'src/Hooks/SmartCRM/useSmartVariables';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';
import { useOutbound } from 'src/Hooks/SmartCRM/useOutbound';
import { convertToBackendPayload } from '@/utils/SmartCRM/backendPayloadConverter';
import { OutboundCampaign } from '@/types/TOutbound';

interface OutboundCampaignModalProps {
	open: boolean;
	onClose: () => void;
	editingTask?: OutboundCampaign | null;
}

const OutboundCampaignModal: React.FC<OutboundCampaignModalProps> = ({
	open,
	onClose,
	editingTask = null,
}) => {
	const isEditing = !!editingTask;
	const [currentStep, setCurrentStep] = useState<number>(0);
	const [showSuccess, setShowSuccess] = useState<boolean>(false);

	const {
		currentCampaign,
		setCurrentCampaign,
		addCampaign,
		resetCurrentCampaign,
		createTaskMutation,
		updateTaskMutation,
	} = useOutbound();
	const { fields } = useSmartVariables(false);
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	useEffect(() => {
		if (open) {
			setShowSuccess(false);
			if (isEditing && editingTask) {
				setCurrentCampaign(editingTask);
				setCurrentStep(0);
			} else {
				const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
				setCurrentCampaign({
					name: '',
					goal: '',
					schedule: {
						startTime: '',
						endTime: '',
						daysOfWeek: [],
						timezone: userTimezone,
					},
					maxRetries: 3,
					workflows: [],
					enrollmentRules: [],
				});
				setCurrentStep(0);
			}
		}
	}, [open, isEditing]);

	const steps = isEditing
		? [{ title: 'Details' }, { title: 'Workflows' }, { title: 'Schedule' }]
		: [{ title: 'Type' }, { title: 'Details' }, { title: 'Workflows' }, { title: 'Schedule' }];

	const getModalConfig = () => {
		const detailsStepIndex = isEditing ? 0 : 1;
		const workflowsStepIndex = isEditing ? 1 : 2;

		if (currentStep === detailsStepIndex || currentStep === workflowsStepIndex) {
			return {
				width: '70vw',
				style: {
					maxWidth: '1400px',
					minWidth: '800px',
				},
			};
		}
		return {
			width: 650,
			style: {},
		};
	};

	const validateCurrentStep = (): boolean => {
		const detailsStepIndex = isEditing ? 0 : 1;
		const scheduleStepIndex = isEditing ? 2 : 3;

		if (currentStep === detailsStepIndex) {
			if (!currentCampaign?.name?.trim()) {
				message.error('Task title is required');
				return false;
			}
			if (!currentCampaign?.goal?.trim()) {
				message.error('Task goal is required');
				return false;
			}
			if (!currentCampaign?.enrollmentRules || currentCampaign.enrollmentRules.length === 0) {
				message.error('At least one enrollment rule is required');
				return false;
			}
		}

		if (currentStep === scheduleStepIndex) {
			if (!currentCampaign?.schedule?.startTime) {
				message.error('Start time is required');
				return false;
			}
			if (!currentCampaign?.schedule?.endTime) {
				message.error('End time is required');
				return false;
			}
			if (currentCampaign?.schedule?.startTime === currentCampaign?.schedule?.endTime) {
				message.error('Start and end times cannot be the same');
				return false;
			}
			if (!currentCampaign?.schedule?.timezone) {
				message.error('Timezone is required');
				return false;
			}
			if (
				!currentCampaign?.schedule?.daysOfWeek ||
				currentCampaign.schedule.daysOfWeek.length === 0
			) {
				message.error('At least one day of the week is required');
				return false;
			}
			if (
				!currentCampaign?.maxRetries ||
				currentCampaign.maxRetries < 1 ||
				currentCampaign.maxRetries > 10
			) {
				message.error('Max retries must be between 1 and 10');
				return false;
			}
		}

		return true;
	};

	const handleNext = (): void => {
		if (!validateCurrentStep()) {
			return;
		}

		if (currentStep < steps.length - 1) {
			setCurrentStep(currentStep + 1);
		}
	};

	const handlePrevious = (): void => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};

	const handleSave = async (): Promise<void> => {
		if (!validateCurrentStep()) {
			return;
		}

		if (!currentCampaign) {
			return;
		}

		try {
			if (isEditing && currentCampaign?.id) {
				const backendPayload = convertToBackendPayload(currentCampaign, fields, selectedAgentId);

				await updateTaskMutation.mutateAsync({
					taskId: currentCampaign.id,
					data: backendPayload,
				});

				setShowSuccess(false);
				setCurrentStep(0);
				resetCurrentCampaign();
				onClose();
			} else {
				const backendPayload = convertToBackendPayload(currentCampaign, fields, selectedAgentId);

				const result = await createTaskMutation.mutateAsync(backendPayload);

				const campaignWithId = {
					...currentCampaign,
					id: result.task.id,
					userId: result.task.userId,
					agentId: result.task.agentId,
					status: result.task.status,
					createdAt: result.task.createdAt,
					updatedAt: result.task.updatedAt,
				};

				addCampaign(campaignWithId);
				setShowSuccess(true);

				setTimeout(() => {
					setShowSuccess(false);
					onClose();
					setCurrentStep(0);
					resetCurrentCampaign();
				}, 3000);
			}
		} catch (error) {
			message.error(error instanceof Error ? error.message : 'Failed to save campaign.');
		}
	};

	const handleClose = () => {
		onClose();
		setCurrentStep(0);
		setShowSuccess(false);
		resetCurrentCampaign();
	};

	const renderStepContent = (): React.ReactNode => {
		if (showSuccess && !isEditing) {
			return <SuccessAnimation visible={showSuccess} />;
		}

		const stepComponents = isEditing
			? [
					<CampaignDetailsStep key="details" />,
					<WorkflowsStep key="workflows" />,
					<ScheduleStep key="schedule" />,
				]
			: [
					<CampaignTypeStep key="type" />,
					<CampaignDetailsStep key="details" />,
					<WorkflowsStep key="workflows" />,
					<ScheduleStep key="schedule" />,
				];

		return stepComponents[currentStep] || null;
	};

	const isLoading = createTaskMutation.isPending || updateTaskMutation.isPending;

	return (
		<Modal
			title={isEditing ? 'Edit Outbound Task' : 'Create Outbound Task'}
			open={open}
			onCancel={handleClose}
			width={getModalConfig().width}
			style={getModalConfig().style}
			footer={null}
			centered
			destroyOnClose
		>
			{!(showSuccess && !isEditing) && (
				<Steps current={currentStep} className="mb-2 mt-4" size="small">
					{steps.map((step, index) => (
						<Steps.Step key={index} title={step.title} />
					))}
				</Steps>
			)}

			<div
				className={
					(isEditing && (currentStep === 0 || currentStep === 1)) ||
					(!isEditing && (currentStep === 1 || currentStep === 2))
						? 'max-h-[75vh] overflow-y-auto'
						: 'min-h-[350px] flex items-center justify-center'
				}
			>
				{renderStepContent()}
			</div>

			{showSuccess && !isEditing ? (
				<div className="flex justify-center pt-4 border-t">
					<Button type="primary" onClick={handleClose}>
						Close
					</Button>
				</div>
			) : (
				<div className="flex justify-between pt-4 border-t">
					<Button onClick={handlePrevious} disabled={currentStep === 0}>
						Previous
					</Button>
					<Button
						type="primary"
						onClick={currentStep < steps.length - 1 ? handleNext : handleSave}
						loading={isLoading}
						disabled={isLoading}
					>
						{currentStep < steps.length - 1 ? 'Next' : isEditing ? 'Update Task' : 'Create Task'}
					</Button>
				</div>
			)}
		</Modal>
	);
};

export default OutboundCampaignModal;
